import type { AuditLog } from '@hostesa/api-client';
import { useFormatters, useT } from '@hostesa/i18n';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  StatusBadge,
  type StatusVariant,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@hostesa/ui';
import { namespace } from './i18n';

const outcomeVariant: Record<string, StatusVariant> = {
  success: 'success',
  executed: 'success',
  failure: 'error',
  denied: 'error',
  blocked: 'warning',
};

/**
 * Outcomes are an open-ended backend enum (executed, success, blocked, denied, …),
 * not fixed UI copy — humanize the raw value (`tool_executed` → `Tool executed`)
 * rather than a per-value i18n key that would be missing for new outcomes.
 */
function outcomeLabel(outcome: string): string {
  const text = outcome.replace(/[_:.]/g, ' ').trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Coerce an unknown nullable field to a display string without leaking objects. */
function asLabel(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

export interface AuditFeedProps {
  /** Newest-first, already PII-redacted rows from the backend. */
  entries: AuditLog[];
  timezone: string;
}

/**
 * PII-REDACTED activity feed. The backend redacts `inputRedacted` /
 * `outputRedacted` and never returns raw guest identifiers, so we render ONLY
 * the structured, safe columns (action, actor type, resource, outcome, time)
 * and never the redacted payload blobs. Rows arrive newest-first.
 */
export function AuditFeed({ entries, timezone }: AuditFeedProps) {
  const { t } = useT(namespace);
  const f = useFormatters(timezone);

  return (
    <Card data-testid="audit-feed">
      <CardHeader>
        <CardTitle>{t('audit.title')}</CardTitle>
        <CardDescription>{t('audit.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {entries.length === 0 ? (
          <EmptyState
            data-testid="audit-feed-empty-state"
            title={t('audit.emptyTitle')}
            description={t('audit.emptyDescription')}
          />
        ) : (
          <Table data-testid="audit-feed-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('audit.column.action')}</TableHead>
                <TableHead>{t('audit.column.actor')}</TableHead>
                <TableHead>{t('audit.column.resource')}</TableHead>
                <TableHead>{t('audit.column.outcome')}</TableHead>
                <TableHead>{t('audit.column.when')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry) => {
                const resourceType = asLabel(entry.resourceType);
                const resourceId = asLabel(entry.resourceId);
                const resource = resourceType
                  ? resourceId
                    ? `${resourceType} · ${resourceId}`
                    : resourceType
                  : '—';
                return (
                  <TableRow key={entry.id} data-testid={`audit-row-${entry.id}`}>
                    <TableCell className="font-medium text-[var(--color-charcoal)]">
                      {entry.action}
                    </TableCell>
                    <TableCell>{entry.actorType}</TableCell>
                    <TableCell className="font-[var(--font-mono)] text-xs">{resource}</TableCell>
                    <TableCell>
                      <StatusBadge
                        data-testid={`audit-outcome-${entry.id}`}
                        status={outcomeVariant[entry.outcome] ?? 'neutral'}
                        label={outcomeLabel(entry.outcome)}
                      />
                    </TableCell>
                    <TableCell>{f.formatDateTime(entry.createdAt)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
